import * as React from 'react'
import { ColumnDef, Table, FilterOptions, PaginationOptions, Checkbox } from 'cs-kevlar-react'
import { CustomerUser, CustomerDevice } from '../../contexts/base'
import { AlertErrorHeader, EditModalActionBar, ErrorDetail } from '..'
import { MenuItem, Select } from '@mui/material'
import { CustomizableInput } from 'components/users/CustomizableInput'

interface AddMultipleDevicesProps {
  customerAgencyId: string
  licensedAgencyId: string
  onAdd: (values: CustomerDevice[]) => void
  onClose: () => void
  users: CustomerUser[]
}

const FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: true,
  disableLiveFiltering: true,
  enableSuggestions: true,
  enableMultipleSearchTerms: true,
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  hidePaginationBar: true,
  disablePaging: true
}

const noUserAssigned = '<No User Assigned>'

type SelectableDevice = CustomerDevice & {user: CustomerUser}

export const AddMultipleDevices: React.FunctionComponent<AddMultipleDevicesProps> = ({
  onAdd,
  customerAgencyId,
  licensedAgencyId,
  onClose,
  users
}) => {

  const columns: ColumnDef<SelectableDevice, string>[] = [
    {
      label: '',
      sortable: false,
      sortByDefault: false,
      filterable: false,
      component: ({ row }) => 
        <Checkbox 
          key={"DeviceSelected"+row.id}
          defaultChecked={false} 
          checked={selectedDeviceIds.includes(row.id)} 
          onChange={(event)=> {handleSelectionCheck(event, row)}}
        />,
      width: 10,
      dataProperty: 'selected' as keyof SelectableDevice,
    },
    {
      label: 'Device Name',
      sortable: true,
      sortByDefault: true,
      sortValueFunc: (row: SelectableDevice) => row.name,
      filterable: true,
      component: ({ row }) => 
        <CustomizableInput 
          key={"DeviceName"+row.id}
          defaultValue={row.name}
          name='Device Name' 
          onChange={(event)=> {handleNameInput(event, row)}}
          required={true} 
        />, 
      dataProperty: 'name' as keyof SelectableDevice,
    },
    {
      label: 'Assigned User',
      sortable: true,
      sortByDefault: false,
      sortValueFunc: (row: SelectableDevice) => row.assignedUserName,
      filterable: true,
      component: ({ row }) => 
        <Select
          key={"AssignedUser"+row.id}
          id={row.id}
          label='Assigned User'
          defaultValue={row.assignedUserName}
          onChange={(event) => onAssignedUserChange(event, row)}
        >
          <MenuItem value={row.user.name}>{row.user.name}</MenuItem>
          <MenuItem value={noUserAssigned}>{noUserAssigned}</MenuItem>
        </Select>,
      dataProperty: 'assignedUserName' as keyof SelectableDevice,
    },
    {
      label: 'Text Activation Key',
      sortable: false,
      sortByDefault: false,
      filterable: false,
      component: ({ row }) => 
        <Checkbox 
          key={"TextActivationKey"+row.id}
          defaultChecked={false} 
          checked={selectedDeviceIdsToText.includes(row.id)} 
          disabled={disabledTextCheckboxes.includes(row.id)} 
          onChange={(event) => {handleTextCheck(event, row)}}
        />,
      width: 10,
      dataProperty: 'textActivation' as keyof SelectableDevice,
    },
  ]
  
  const handleSelectionCheck = (chkValue: any, row: SelectableDevice) => {

    if (chkValue.target.checked)
    {
      const selectedIds = [...selectedDeviceIds, row.id]
      setSelectedDeviceIds(selectedIds)
    }
    else 
    {
      const selectedIds = selectedDeviceIds.filter(item => item !== row.id)
      setSelectedDeviceIds(selectedIds)
    }
  }
  
  const handleNameInput = (value: any, row: SelectableDevice) => {
    row.name = value.target.value
  }
  
  const onAssignedUserChange = (value: any, row: SelectableDevice) => {
    row.assignedUserName = (value.target.value as string)
  
    if (value.target.value === noUserAssigned)
    {
      row.assignedUserId = ''
      row.sendPinToAssignedUser = false
      setSelectedDeviceIdsToText(selectedDeviceIdsToText.filter(item => item !== row.id))
      setDisabledTextCheckboxes([...disabledTextCheckboxes, row.id])
    }
    else
    {
      row.assignedUserId = row.user.id
      setDisabledTextCheckboxes(disabledTextCheckboxes.filter(item => item !== row.id))
    }

    updateTextAllSelected()
  }
  
  const handleTextCheck = (chkValue: any, row: SelectableDevice) => {
    if (row.assignedUserName === noUserAssigned)
      return
  
    row.sendPinToAssignedUser = chkValue.target.checked

    if (!chkValue.target.checked)
    {
      setSelectedDeviceIdsToText(selectedDeviceIdsToText.filter(item => item !== row.id))
      setTextAllSelected(false)
    }
    else
    {
      setSelectedDeviceIdsToText([...selectedDeviceIdsToText, row.id])
      updateTextAllSelected()
    }
  }

  const updateTextAllSelected = () => {
    var selectedCount = selectableDevices.filter(d => d.sendPinToAssignedUser === true).length
    var assignedUserCount = selectableDevices.filter(d => d.assignedUserName != noUserAssigned).length

    if (selectedCount == assignedUserCount)
    {
      if (!textAllSelected)
        setTextAllSelected(true)
    }
    else if (textAllSelected)
      setTextAllSelected(false)
  }  

  const initialSelectableDevices = () => {
    const devices = users.map(user => {  
      return {
        id: user.id,
        name: user.fullName,
        assignedUserName: user.name,
        assignedUserId: user.id,
        customerAgencyId: customerAgencyId,
        customerDeviceId: customerAgencyId,
        licensedAgencyId: licensedAgencyId,
        email: user.email,
        pin: '',
        pinExpiresOn: '',
        pinExpired: false,
        pinUsed: false,
        notes: '',
        replaceConfirmed: false,
        sendPinToAssignedUser: false,
        properties: {},
        loggingConfiguration: {
          verboseLoggingEnabled: false,
          enabledTypes: [],
        },
        certificateConfiguration: {
          allowManualCertificateRenewal: false,
          certificateRenewalWindowInSecondsOverride: null,
        },
        selected: false,
        user: user
      }
    })

    return devices
  }

  const [selectableDevices, setSelectableDevices] = React.useState<SelectableDevice[]>(initialSelectableDevices)
  const [disabledTextCheckboxes, setDisabledTextCheckboxes] = React.useState<string[]>([])
  const [showErrorBanner, setShowErrorBanner] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorDetail[]>([])
  const [textAllSelected, setTextAllSelected] = React.useState(false)
  const [selectedDeviceIds, setSelectedDeviceIds] = React.useState<string[]>([])
  const [selectedDeviceIdsToText, setSelectedDeviceIdsToText] = React.useState<string[]>([])

  const onSelectAll = () => {
    let deviceIds: string[] = []
    if(selectedDeviceIds.length != selectableDevices.length)
    {
      deviceIds = selectableDevices.map(x => x.id)
    }
    setSelectedDeviceIds(deviceIds)
  }

  const onTextAll = () => {
    var devices = selectableDevices.map((device) => {
      return {
        ...device,
        sendPinToAssignedUser: device.assignedUserName != noUserAssigned ? !textAllSelected : false
      }
    })

    setSelectedDeviceIdsToText(devices.filter(x => x.sendPinToAssignedUser).map(x => x.id))
    setTextAllSelected(!textAllSelected)
    setSelectableDevices(devices)
  }

  const onSelectDevices = () => {

    if (selectedDeviceIds.length == 0) {
      onClose()
      return
    }

    const errors: ErrorDetail[] = []
    const selectedDevices = selectableDevices.filter(x => selectedDeviceIds.includes(x.id))

    selectedDevices.forEach((device) => {
      if (device.name == null || device.name == '')
      {
        var error: ErrorDetail = {
        errorIdentifierText: device.name == null || device.name == '' ? 'No Assigned Name' : device.name,
        error: 'Device name is required',
        id: device.id
        }

        errors.push(error)
      }
    })

    if (errors.length == 0)
    {
      onAdd(selectedDevices)
    }
    else
    {
      setShowErrorBanner(errors.length > 0)
      setErrorMessage(errors.length + ' device' + (errors.length > 1 ? 's' : '') + ' failed validation')
      setErrors(errors)
    }
  }

  return (
    <React.Fragment>
      {showErrorBanner && 
        <AlertErrorHeader errorLabel='Device Name' errorMessage={errorMessage} errors={errors} />
      }
      <Table
        data={selectableDevices}
        filterOptions={FILTER_OPTIONS}
        paginationOptions={PAGINATION_OPTIONS}
        columns={columns as ColumnDef<SelectableDevice, string>[]}
        ariaLabel="Devices to Add"
        useDefaultRow
        uniqueIdentifier="AddMultipleDevicesList"
      />
      <EditModalActionBar
        canDelete={true}
        deleteText={selectedDeviceIds.length == selectableDevices.length ? 'Unselect All' : 'Select All'}
        onDelete={onSelectAll}
        onCancel={onClose}
        onSave={onSelectDevices}          
        canSubmit={selectedDeviceIds.length > 0}
        saveButtonType='button'
        saveText='Add'
        canSecondaryAction={true}
        secondaryActionText={textAllSelected ? 'Don\'t Text Any' : 'Text All'}
        onSecondaryAction={onTextAll}
      />
    </React.Fragment>
  )
}
